import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from './store/slices/authSlice';
import api from './services/api';

// Layouts (small — always needed, not lazy)
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';

// Components (always needed)
import ProtectedRoute from './components/auth/ProtectedRoute';
import NotificationListener from './components/notifications/NotificationListener';
import SiteTracker from './components/common/SiteTracker';
import Loader from './components/common/Loader';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import PWAUpdatePrompt from './components/common/PWAUpdatePrompt';

// ── Lazy-loaded pages ────────────────────────────────────────────────────────
// Each page is split into its own JS chunk and only downloaded when first visited.

// Public / Auth
const HomePage            = lazy(() => import('./pages/public/HomePage'));
const LoginPage           = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage        = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage  = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage   = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage     = lazy(() => import('./pages/auth/VerifyEmailPage'));
const BookingPage         = lazy(() => import('./pages/public/BookingPage'));
const ServicesPage        = lazy(() => import('./pages/public/ServicesPage'));
const FleetPage           = lazy(() => import('./pages/public/FleetPage'));
const CoverageAreasPage   = lazy(() => import('./pages/public/CoverageAreasPage'));
const AboutPage           = lazy(() => import('./pages/public/AboutPage'));
const ContactPage         = lazy(() => import('./pages/public/ContactPage'));
const FAQPage             = lazy(() => import('./pages/public/FAQPage'));
const TermsPage           = lazy(() => import('./pages/public/TermsPage'));
const PrivacyPage         = lazy(() => import('./pages/public/PrivacyPage'));
const BlogPage            = lazy(() => import('./pages/public/BlogPage'));
const BlogPostPage        = lazy(() => import('./pages/public/BlogPostPage'));
const RoutesPage          = lazy(() => import('./pages/public/RoutesPage'));
const RoutePage           = lazy(() => import('./pages/public/RoutePage'));
const AirportTransferPage = lazy(() => import('./pages/public/AirportTransferPage'));

// Passenger
const PassengerDashboard  = lazy(() => import('./pages/passenger/PassengerDashboard'));
const MyBookings          = lazy(() => import('./pages/passenger/MyBookings'));
const BookingDetails      = lazy(() => import('./pages/passenger/BookingDetails'));
const PassengerProfile    = lazy(() => import('./pages/passenger/PassengerProfile'));
const PaymentPage         = lazy(() => import('./pages/passenger/PaymentPage'));

// Driver
const DriverDashboard         = lazy(() => import('./pages/driver/DriverDashboard'));
const DriverRides             = lazy(() => import('./pages/driver/DriverRides'));
const DriverProfile           = lazy(() => import('./pages/driver/DriverProfile'));
const DriverEarnings          = lazy(() => import('./pages/driver/DriverEarnings'));
const DriverRatings           = lazy(() => import('./pages/driver/DriverRatings'));
const DriverOnboarding        = lazy(() => import('./pages/driver/DriverOnboarding'));
const DriverVehicleSubmission = lazy(() => import('./pages/driver/DriverVehicleSubmission'));

// Admin
const AdminDashboard            = lazy(() => import('./pages/admin/AdminDashboard'));
const ManageUsers               = lazy(() => import('./pages/admin/ManageUsers'));
const ManageDrivers             = lazy(() => import('./pages/admin/ManageDrivers'));
const ManageRides               = lazy(() => import('./pages/admin/ManageRides'));
const ManageVehicles            = lazy(() => import('./pages/admin/ManageVehicles'));
const ManagePayments            = lazy(() => import('./pages/admin/ManagePayments'));
const ManageRates               = lazy(() => import('./pages/admin/ManageRates'));
const CommissionSettings        = lazy(() => import('./pages/admin/CommissionSettings'));
const TimezoneSettings          = lazy(() => import('./pages/admin/TimezoneSettings'));
const RegistrationSettings      = lazy(() => import('./pages/admin/RegistrationSettings'));
const MaintenanceSettings       = lazy(() => import('./pages/admin/MaintenanceSettings'));
const Analytics                 = lazy(() => import('./pages/admin/Analytics'));
const SiteAnalytics             = lazy(() => import('./pages/admin/SiteAnalytics'));
const AdminVerifications        = lazy(() => import('./pages/admin/AdminVerifications'));
const RatingsAndReviews         = lazy(() => import('./pages/admin/RatingsAndReviews'));
const ProfilePictureVerification= lazy(() => import('./pages/admin/ProfilePictureVerification'));
const ManageBloggers            = lazy(() => import('./pages/admin/ManageBloggers'));
const ManageBlogPosts           = lazy(() => import('./pages/admin/ManageBlogPosts'));
const AdminProfile              = lazy(() => import('./pages/admin/AdminProfile'));
const OfflineBooking            = lazy(() => import('./pages/admin/OfflineBooking'));

// Shared
const CampaignLinkGenerator = lazy(() => import('./pages/shared/CampaignLinkGenerator'));

// Blogger
const BloggerDashboard  = lazy(() => import('./pages/blogger/BloggerDashboard'));
const BloggerPosts      = lazy(() => import('./pages/blogger/BloggerPosts'));
const BloggerProfile    = lazy(() => import('./pages/blogger/BloggerProfile'));

// ── Suspense fallback ────────────────────────────────────────────────────────
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader size="large" />
  </div>
);

function App() {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);

  // Validate stored token against the current backend on every app load.
  // This auto-clears stale tokens (e.g. from a different server/environment).
  useEffect(() => {
    if (token) {
      api.get('/user').catch((error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          dispatch(logout());
        }
      });
    }
  }, []);

  return (
    <>
      <PWAInstallPrompt />
      <PWAUpdatePrompt />
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Luxury Pages (Standalone with their own Navbar/Footer) */}
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/coverage" element={<CoverageAreasPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/routes" element={<RoutesPage />} />
        <Route path="/routes/:slug" element={<RoutePage />} />
        <Route path="/airport-transfer" element={<AirportTransferPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Auth Routes removed from PublicLayout - now standalone */}

        {/* Passenger Routes */}
        <Route
          path="/passenger"
          element={
            <ProtectedRoute allowedRoles={['passenger']}>
              <PrivateLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/passenger/dashboard" replace />} />
          <Route path="dashboard" element={<PassengerDashboard />} />
          <Route path="bookings" element={<MyBookings />} />
          <Route path="bookings/:id" element={<BookingDetails />} />
          <Route path="payment" element={<PaymentPage />} />
          <Route path="profile" element={<PassengerProfile />} />
        </Route>

        {/* Driver Onboarding (without PrivateLayout) */}
        <Route
          path="/driver/onboarding"
          element={
            <ProtectedRoute allowedRoles={['driver']}>
              <DriverOnboarding />
            </ProtectedRoute>
          }
        />

        {/* Driver Routes */}
        <Route
          path="/driver"
          element={
            <ProtectedRoute allowedRoles={['driver']}>
              <PrivateLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/driver/dashboard" replace />} />
          <Route path="dashboard" element={<DriverDashboard />} />
          <Route path="rides" element={<DriverRides />} />
          <Route path="earnings" element={<DriverEarnings />} />
          <Route path="ratings" element={<DriverRatings />} />
          <Route path="profile" element={<DriverProfile />} />
          <Route path="submit-vehicle" element={<DriverVehicleSubmission />} />
        </Route>

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PrivateLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="users" element={<ManageUsers />} />
          <Route path="drivers" element={<ManageDrivers />} />
          <Route path="rides" element={<ManageRides />} />
          <Route path="vehicles" element={<ManageVehicles />} />
          <Route path="payments" element={<ManagePayments />} />
          <Route path="rates" element={<ManageRates />} />
          <Route path="commission" element={<CommissionSettings />} />
          <Route path="timezone" element={<TimezoneSettings />} />
          <Route path="registration" element={<RegistrationSettings />} />
          <Route path="maintenance" element={<MaintenanceSettings />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="site-analytics" element={<SiteAnalytics />} />
          <Route path="verifications" element={<AdminVerifications />} />
          <Route path="profile-pictures" element={<ProfilePictureVerification />} />
          <Route path="ratings" element={<RatingsAndReviews />} />
          <Route path="bloggers" element={<ManageBloggers />} />
          <Route path="blog-posts" element={<ManageBlogPosts />} />
          <Route path="campaign-links" element={<CampaignLinkGenerator />} />
          <Route path="offline-booking" element={<OfflineBooking />} />
          <Route path="profile" element={<AdminProfile />} />
        </Route>

        {/* Blogger Routes */}
        <Route
          path="/blogger"
          element={
            <ProtectedRoute allowedRoles={['blogger', 'admin']}>
              <PrivateLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/blogger/dashboard" replace />} />
          <Route path="dashboard" element={<BloggerDashboard />} />
          <Route path="posts" element={<BloggerPosts />} />
          <Route path="posts/new" element={<BloggerPosts />} />
          <Route path="posts/edit/:id" element={<BloggerPosts />} />
          <Route path="campaign-links" element={<CampaignLinkGenerator />} />
          <Route path="profile" element={<BloggerProfile />} />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>

      {/* Site visit tracker — fires on every route change */}
      <SiteTracker />

      {/* Global notification listener — shows toasts for new notifications */}
      <NotificationListener />

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

export default App;
